
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const BlogPage = () => {
  const blogPosts = [
    {
      title: 'The Making of Family Secrets',
      category: 'Writing Process',
      date: 'March 15, 2024',
      readTime: '5 min read',
      excerpt:
        'Discover the inspiration behind my latest novel and the journey of bringing these characters to life. From initial concept to final manuscript, explore the creative process that shaped this San Francisco saga.',
      image: 'https://public.readdy.ai/ai/img_res/6aaeeb4f6a030c160bf43b650dd05ba2.jpg',
    },
    {
      title: 'San Francisco Through the Decades',
      category: 'History',
      date: 'March 8, 2024',
      readTime: '7 min read',
      excerpt:
        'Exploring how the City by the Bay has transformed over generations while maintaining its unique character. A deep dive into the historical events and cultural shifts that shaped the backdrop of my novels.',
      image: 'https://readdy.ai/api/search-image?query=historic%20black%20and%20white%20photograph%20of%20San%20Francisco%20cable%20cars%20and%20Victorian%20houses%20on%20steep%20hills%20with%20vintage%20street%20lamps%20creating%20a%20timeless%20nostalgic%20atmosphere&width=600&height=400&seq=blog-post-history-1&orientation=landscape',
    },
    {
      title: 'Character Development in Family Sagas',
      category: 'Craft',
      date: 'February 28, 2024',
      readTime: '6 min read',
      excerpt:
        'How to create multi-generational characters that resonate with readers across different time periods. Techniques for maintaining character authenticity while spanning decades of family history.',
      image: 'https://readdy.ai/api/search-image?query=open%20vintage%20family%20photo%20album%20with%20sepia%20photographs%20and%20handwritten%20notes%20on%20antique%20wooden%20table%20with%20soft%20natural%20lighting%20creating%20an%20intimate%20nostalgic%20mood&width=600&height=400&seq=blog-post-craft-1&orientation=landscape',
    },
    {
      title: 'Research and Historical Accuracy',
      category: 'Writing Process',
      date: 'February 20, 2024',
      readTime: '8 min read',
      excerpt:
        'The importance of thorough research when writing historical fiction. Learn about my process for ensuring authenticity while crafting compelling narratives set in different eras of San Francisco history.',
      image: 'https://readdy.ai/api/search-image?query=vintage%20library%20with%20old%20books%20stacked%20on%20wooden%20table%20antique%20reading%20lamp%20and%20historical%20documents%20creating%20scholarly%20atmosphere%20with%20warm%20ambient%20lighting&width=600&height=400&seq=blog-post-research-1&orientation=landscape',
    },
    {
      title: 'The Golden Gate Bridge in Literature',
      category: 'San Francisco',
      date: 'February 12, 2024',
      readTime: '5 min read',
      excerpt:
        'How San Francisco\'s most iconic landmark has served as both setting and symbol in countless stories. Exploring the bridge\'s role in shaping the city\'s literary identity and its significance in my own work.',
      image: 'https://readdy.ai/api/search-image?query=artistic%20view%20of%20Golden%20Gate%20Bridge%20at%20sunset%20with%20dramatic%20orange%20sky%20and%20fog%20rolling%20through%20creating%20iconic%20San%20Francisco%20atmosphere%20with%20vintage%20film%20photography%20aesthetic&width=600&height=400&seq=blog-post-bridge-1&orientation=landscape',
    },
    {
      title: 'Writing Authentic Dialogue Across Generations',
      category: 'Craft',
      date: 'February 5, 2024',
      readTime: '6 min read',
      excerpt:
        'Techniques for capturing the unique voice and speech patterns of characters from different time periods. How to make dialogue feel authentic without becoming dated or difficult to read.',
      image: 'https://readdy.ai/api/search-image?query=two%20people%20having%20conversation%20in%20vintage%20cafe%20with%20warm%20lighting%20and%20retro%20decor%20creating%20intimate%20storytelling%20atmosphere%20with%20soft%20focus%20and%20nostalgic%20mood&width=600&height=400&seq=blog-post-dialogue-1&orientation=landscape',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-[#F8F6F3]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-serif text-6xl text-[#2C2C2C] mb-6">
              Author's Journal
            </h1>
            <div className="flex items-center justify-center mb-6">
              <div className="h-0.5 w-32 bg-[#C9A961]"></div>
            </div>
            <p className="text-xl text-[#666666] leading-relaxed">
              Insights on the writing process, San Francisco history, character development, 
              and the craft of storytelling
            </p>
          </motion.div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <motion.article
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
              >
                <div className="relative h-56 w-full overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                  <div className="absolute top-4 left-4">
                    <span className="px-4 py-1 bg-[#8B2635] text-white text-xs rounded-full whitespace-nowrap">
                      {post.category}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <h2 className="font-serif text-2xl text-[#2C2C2C] mb-3 line-clamp-2 hover:text-[#8B2635] transition-colors">
                    {post.title}
                  </h2>

                  <div className="flex items-center text-sm text-[#666666] mb-4">
                    <span>{post.date}</span>
                    <span className="mx-2 text-[#C9A961]">•</span>
                    <span>{post.readTime}</span>
                  </div>

                  <p className="text-[#666666] leading-relaxed mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>

                  <div className="flex items-center justify-between">
                    <button className="inline-flex items-center text-[#8B2635] hover:text-[#6d1e29] transition-colors font-medium whitespace-nowrap">
                      Read More
                      <i className="ri-arrow-right-line ml-2"></i>
                    </button>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <button className="px-8 py-3 border-2 border-[#8B2635] text-[#8B2635] rounded-lg hover:bg-[#8B2635] hover:text-white transition-all whitespace-nowrap cursor-pointer">
              Load More Posts
            </button>
          </motion.div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-[#F8F6F3]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-12 text-center"
          >
            <h2 className="font-serif text-4xl text-[#2C2C2C] mb-4">
              Subscribe to Updates
            </h2>
            <p className="text-lg text-[#666666] mb-8">
              Get the latest blog posts and writing insights delivered to your inbox
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Your email address"
                className="flex-1 px-6 py-3 rounded-full border-2 border-gray-300 focus:border-[#8B2635] focus:outline-none text-sm"
              />
              <button className="px-8 py-3 bg-[#C9A961] text-white rounded-full hover:bg-[#b89850] transition-all shadow-md whitespace-nowrap cursor-pointer">
                Subscribe
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;
